import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { blocklistService } from '@/features/onboarding/services/blocklist.service';
import type {
  BlocklistCategoriesResponse,
  BlocklistQueryParams,
  BlocklistListResponse,
  CreateBlocklistRequest,
  CreateBlocklistResponse,
  DeactivateBlocklistRequest,
  DeactivateBlocklistResponse,
} from '@/features/onboarding/types';

/**
 * Query key factory for blocklist
 */
export const blocklistKeys = {
  all: ['blocklist'] as const,
  lists: () => [...blocklistKeys.all, 'list'] as const,
  list: (params: BlocklistQueryParams) => [...blocklistKeys.lists(), params] as const,
  categories: () => [...blocklistKeys.all, 'categories'] as const,
};

/**
 * Hook to fetch blocklist items with pagination and filtering
 */
export const useBlocklist = (params: BlocklistQueryParams = {}) => {
  return useQuery<BlocklistListResponse>({
    queryKey: blocklistKeys.list(params),
    queryFn: () => blocklistService.getList(params),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch blocklist categories
 */
export const useBlocklistCategories = () => {
  return useQuery<BlocklistCategoriesResponse>({
    queryKey: blocklistKeys.categories(),
    queryFn: () => blocklistService.getCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes - categories rarely change
  });
};

/**
 * Hook to create a new blocklist item
 */
export const useCreateBlocklist = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateBlocklistResponse, Error, CreateBlocklistRequest>({
    mutationFn: (data) => blocklistService.create(data),
    onSuccess: () => {
      // Invalidate all blocklist queries to refetch data
      void queryClient.invalidateQueries({ queryKey: blocklistKeys.all });
    },
  });
};

/**
 * Hook to deactivate a blocklist item
 */
export const useDeactivateBlocklist = () => {
  const queryClient = useQueryClient();

  return useMutation<DeactivateBlocklistResponse, Error, DeactivateBlocklistRequest>({
    mutationFn: (data) => blocklistService.deactivate(data),
    onSuccess: () => {
      // Invalidate all blocklist queries to refetch data
      void queryClient.invalidateQueries({ queryKey: blocklistKeys.all });
    },
  });
};
